import type { ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import { Breadcrumbs, Heading, Paragraph } from '@digdir/designsystemet-react'
import { useT } from '@/shared/i18n'

export type BreadcrumbItem = {
  /** Display label for this crumb. Should already be i18n'd by the caller. */
  label: string
  /**
   * If present the crumb renders as an <a href>. Absent (or undefined) means
   * this is the current page — rendered as .crumb__current with
   * aria-current="page".
   */
  href?: string
}

export type PageHeaderProps = {
  /** Centered overview heading with actions beneath the title. */
  centered?: boolean
  /** Page title rendered as a visible <h1>. Must be pre-i18n'd by caller. */
  title: string
  /** Embedded gallery examples use a subordinate heading. Real pages default to h1. */
  headingLevel?: 1 | 2 | 3
  /** Optional supporting text rendered below the title. Pre-i18n'd. */
  subtitle?: string
  /**
   * Optional breadcrumb trail. Empty array is treated the same as absent
   * (no .crumb nav rendered). Last item is the current page; all others
   * should have href.
   */
  breadcrumb?: BreadcrumbItem[]
  /**
   * Optional action slot rendered in .page-hd__actions. Typically one or more
   * <Button> elements supplied by the page. When absent the actions container
   * is not rendered.
   */
  actions?: ReactNode
}

export function PageHeader({ title, subtitle, breadcrumb, actions, centered = false, headingLevel = 1 }: PageHeaderProps) {
  const t = useT()
  const hasBreadcrumb = Array.isArray(breadcrumb) && breadcrumb.length > 0

  return (
    <div className={centered ? 'page-hd page-hd--centered' : 'page-hd'}>
      <div>
        {hasBreadcrumb && (
          // The DS custom element marks the last anchor current even when a trailing
          // text crumb is the current page. Native nav preserves the owner's semantics.
          <nav className="ds-breadcrumbs crumb" aria-label={t('chrome.breadcrumb.label')}>
            {breadcrumb.length === 1 && breadcrumb[0]?.href ? <Breadcrumbs.Link asChild><Link href={breadcrumb[0].href}>{breadcrumb[0].label}</Link></Breadcrumbs.Link> : <Breadcrumbs.List>
            {(breadcrumb as BreadcrumbItem[]).map((item, idx) => {
              const isLast = idx === (breadcrumb as BreadcrumbItem[]).length - 1
              return (
                <Breadcrumbs.Item key={item.label + String(idx)}>
                  {item.href != null ? (
                    <Breadcrumbs.Link asChild><Link href={item.href}>{item.label}</Link></Breadcrumbs.Link>
                  ) : (
                    <span className="crumb__current" aria-current={isLast ? 'page' : undefined}>
                      {item.label}
                    </span>
                  )}
                </Breadcrumbs.Item>
              )
            })}
            </Breadcrumbs.List>}
          </nav>
        )}

        <Heading level={headingLevel} data-size="md" className="page-hd__title">{title}</Heading>

        {subtitle != null && (
          <Paragraph data-size="sm" className="page-hd__sub">{subtitle}</Paragraph>
        )}
      </div>

      {actions != null && (
        <div className="page-hd__actions">{actions}</div>
      )}
    </div>
  )
}
